import { toRichText } from './mappers';
import { isFamilyList } from './unsweep';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Portable Text, told apart by its blocks: every item a typed object, and at least one of them a
 * `block`. That is the line `isSlotArray` draws from the other side, so no array is ever both.
 */
const isPortableText = (value: unknown[]): boolean =>
  value.some(item => isObject(item) && item._type === 'block') &&
  value.every(item => isObject(item) && typeof item._type === 'string');

const serialize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    if (isPortableText(value)) return toRichText(value) ?? '';
    // Kept as stored: `richTextByRole` and `familyEntryByRole` read these lists raw, and the entries
    // they hold are offered under their own names anyway, where this does reach them.
    if (isFamilyList(value)) return value;
    return value.map(item => serialize(item));
  }
  if (isObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, member]) => [key, serialize(member)]));
  }
  return value;
};

/**
 * Hands a block's rich text to components as an HTML string, wherever in the block it sits.
 *
 * Sitecore sends rich text as HTML and Sanity as Portable Text, and until now the adapter passed
 * both through, so every component had to call `toRichText` on every field. One that did not
 * rendered raw blocks on this lane, or threw. Converting here means a component reads the same
 * string from either CMS and needs no adapter call at all. Components that still make one are
 * unaffected: `toRichText` passes an HTML string through unchanged and returns `undefined` for `''`,
 * exactly what it returned for the blocks.
 *
 * It walks the whole block rather than its top level, because rich text also sits inside objects:
 * a notification's `content`, a dialog's `content`, a radio group's `hint`. The one thing it leaves
 * alone is a family list. See `unsweepFamilies`, which runs first and offers each of those entries
 * under its own name.
 *
 * An empty field is left as it is. Stored as `[]`, it cannot be told from an empty list without the
 * schema. The migration leaves empty rich text out, so it reads as absent; a field that still arrives
 * as `[]` is a fix for the data, rather than a list of field names to keep here.
 *
 * Returns new objects and never touches the payload it was given.
 */
export const serializeRichText = (content: Record<string, unknown>): Record<string, unknown> =>
  serialize(content) as Record<string, unknown>;
